import { useEffect, useMemo, useState } from 'react';
import { UMButton, UMIcon, UMInput } from '../../urbanmap/_components';
import { PERIODS, USAGES, countModifiedRatios, isValidRatio } from './domain';
import { PC, UMR, caption } from './tokens';
import type { PeriodKey, RatioTable as Ratios, UsageKey } from './types';
import { Pill } from '../proposition-c/ui';

interface Props {
  value: Ratios;
  defaults: Ratios;
  onChange: (next: Ratios) => void;
  onValidityChange?: (valid: boolean) => void;
}

const keyOf = (u: UsageKey, p: PeriodKey) => `${u}.${p}`;
const parse = (s: string) => (s.trim() === '' ? NaN : Number(s.replace(',', '.')));

const th = { padding: '8px 6px', fontSize: 12, lineHeight: '16px', fontWeight: 700, color: UMR.textLabel, textAlign: 'left' as const, borderBottom: `1px solid ${UMR.border}` };

/** Tableau usage × période éditable en ligne (kWh/m²/an), avec marqueurs « modifié » / « invalide ». */
export function RatioTable({ value, defaults, onChange, onValidityChange }: Props) {
  const [texts, setTexts] = useState<Record<string, string>>({});
  const invalid = useMemo(() => Object.entries(texts).filter(([, t]) => !isValidRatio(parse(t))).map(([k]) => k), [texts]);
  const modified = countModifiedRatios(value, defaults);

  useEffect(() => { onValidityChange?.(invalid.length === 0); }, [invalid.length, onValidityChange]);

  const set = (u: UsageKey, p: PeriodKey, raw: string) => {
    setTexts((t) => ({ ...t, [keyOf(u, p)]: raw }));
    const n = parse(raw);
    if (isValidRatio(n)) onChange({ ...value, [u]: { ...value[u], [p]: n } });
  };
  const resetCell = (u: UsageKey, p: PeriodKey) => {
    setTexts((t) => { const n = { ...t }; delete n[keyOf(u, p)]; return n; });
    onChange({ ...value, [u]: { ...value[u], [p]: defaults[u][p] } });
  };
  const resetAll = () => {
    setTexts({});
    onChange(Object.fromEntries(USAGES.map((u) => [u.key, { ...defaults[u.key] }])) as Ratios);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontFamily: UMR.font }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
          <strong>Ratios de consommation</strong>
          <span style={caption}>kWh/m²/an · chauffage + ECS · appliqués à tout le projet</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {modified > 0 && <Pill tone="accent" icon="edit">{modified} modifié{modified > 1 ? 's' : ''}</Pill>}
          <UMButton variant="subtle" icon="restart_alt" disabled={modified === 0 && invalid.length === 0} onClick={resetAll}>Réinitialiser</UMButton>
        </div>
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', fontSize: 14 }}>
        <colgroup>
          <col style={{ width: 170 }} />
          {PERIODS.map((p) => <col key={p.key} />)}
        </colgroup>
        <thead>
          <tr style={{ background: UMR.grey100 }}>
            <th style={th}>Usage \ Période</th>
            {PERIODS.map((p) => <th key={p.key} style={{ ...th, textAlign: 'center' }}>{p.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {USAGES.map((u) => (
            <tr key={u.key} style={{ borderBottom: `1px solid ${UMR.borderSubtle}` }}>
              <td style={{ padding: '6px', fontWeight: 600 }}>{u.label}</td>
              {PERIODS.map((p) => {
                const k = keyOf(u.key, p.key);
                const text = texts[k] ?? String(value[u.key][p.key]);
                const bad = invalid.includes(k);
                const changed = !bad && value[u.key][p.key] !== defaults[u.key][p.key];
                return (
                  <td
                    key={p.key}
                    title={bad ? 'Valeur invalide : nombre entre 1 et 1 000' : `Défaut : ${defaults[u.key][p.key]} kWh/m²/an`}
                    style={{ padding: 4, verticalAlign: 'top', background: bad ? PC.dangerBg : changed ? PC.accentBg : 'transparent', borderLeft: `3px solid ${bad ? PC.dangerText : changed ? PC.accent : 'transparent'}` }}
                  >
                    <UMInput type="number" min={1} value={text} ariaLabel={`Ratio ${u.label} ${p.label}`} onChange={(v) => set(u.key, p.key, v)} />
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 16, marginTop: 2, fontSize: 11, lineHeight: '14px' }}>
                      {bad && <span style={{ color: PC.dangerText }}>Invalide</span>}
                      {changed && (
                        <>
                          <span style={{ color: PC.accentStrong }}>déf. {defaults[u.key][p.key]}</span>
                          <button
                            type="button"
                            aria-label={`Rétablir la valeur par défaut ${u.label} ${p.label}`}
                            title="Rétablir la valeur par défaut"
                            onClick={() => resetCell(u.key, p.key)}
                            style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', display: 'flex' }}
                          >
                            <UMIcon name="undo" size={14} color={PC.accentStrong} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, ...caption }}>
        <UMIcon name="info" size={14} color={UMR.textSecondary} />
        Valeurs par défaut : valeurs de démonstration, à fournir par le métier.
        {invalid.length > 0 && <span style={{ color: PC.dangerText, fontWeight: 700, marginLeft: 8 }}>{invalid.length} valeur{invalid.length > 1 ? 's' : ''} invalide{invalid.length > 1 ? 's' : ''} (1 à 1 000)</span>}
      </div>
    </div>
  );
}
